import { z } from 'zod';
/**
 * Zod schemas for every entity in PRD 03 (Data Model). These are the
 * single source of truth for the wire shapes every later milestone
 * consumes — the API validates rows on the way out, the typed web
 * client validates responses on the way in, and both packages share
 * the derived TypeScript types.
 *
 * Each entity schema here mirrors a table defined by a migration in
 * `migrations/`. The enum schemas mirror the Postgres enum values
 * exactly; drift between this file and the migrations is pinned by
 * the test suite in `entities.test.ts`.
 *
 * Conventions:
 *   - bigserial / bigint PKs and FKs use `idSchema`, which accepts
 *     number, bigint, or a decimal string. The pg driver returns
 *     bigint-wide columns as strings by default; callers that have
 *     parsed them to JS number (safe for ids below 2^53) or bigint
 *     are also valid.
 *   - `timestamptz` values are typed as ISO-8601 strings (with or
 *     without offset) OR `Date` instances. No implicit timezone
 *     assumption — rows from the DB arrive as `Date`; rows from JSON
 *     arrive as strings.
 *   - `date` values are typed as `YYYY-MM-DD` strings or `Date`.
 *   - `citext` columns (emails) are case-insensitive at the DB layer;
 *     the schemas accept any-case input and record it as-is — the
 *     DB's collation does the matching.
 *   - SHA-256 and HMAC hex columns are guarded by a lowercase-hex
 *     64-character format check (mirroring the DB CHECK constraints).
 *   - Nullable columns use `.nullable()` (not `.optional()`); with
 *     `exactOptionalPropertyTypes: true` in tsconfig, the emitted
 *     type is `T | null`, not `T | null | undefined`.
 */
// -----------------------------------------------------------------------------
// Primitive helpers
// -----------------------------------------------------------------------------
/**
 * bigserial / bigint PK or FK. Accepts:
 *   - number (safe for ids below 2^53 — most of our table lifetimes);
 *   - bigint (the canonical form);
 *   - decimal string (pg driver default for bigint columns).
 */
export const idSchema = z.union([
    z.number().int().nonnegative(),
    z.bigint().nonnegative(),
    z.string().regex(/^\d+$/, 'must be a non-negative integer as a decimal string'),
]);
/** ISO 8601 `YYYY-MM-DD` calendar date, or a `Date` instance. */
export const isoDateSchema = z.union([
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO-8601 date (YYYY-MM-DD)'),
    z.date(),
]);
/** ISO 8601 timestamp with or without offset, or a `Date` instance. */
export const isoDateTimeSchema = z.union([z.string().datetime({ offset: true }), z.date()]);
/** 64 lowercase hex characters — format of SHA-256 / HMAC columns. */
export const hex64Schema = z.string().regex(/^[0-9a-f]{64}$/, 'must be 64 lowercase hex chars');
/**
 * citext email column. Stored case-insensitively by the DB; the schema
 * keeps the caller's casing verbatim — `.transform(s => s.toLowerCase())`
 * would fight the DB's uniqueness semantics if a caller ever needed to
 * round-trip a value and compare against a freshly-fetched row.
 */
export const citextEmailSchema = z.string().min(1).max(320);
/** inet column. Accepts any non-empty string; strict IPv4/IPv6 parsing
 *  is left to the DB (Postgres rejects malformed inets on insert). */
export const inetSchema = z.string().min(1);
// -----------------------------------------------------------------------------
// Enum schemas — values mirror the Postgres enum types exactly.
// -----------------------------------------------------------------------------
/** `person_type` — migration 0005. `staff` is reserved for the M21 rollout. */
export const personTypeEnum = z.enum(['volunteer', 'practicum_student', 'observer', 'staff']);
/** `person_status` — migration 0005. Single-step archive: any non-`inactive`
 *  status flips directly to `inactive` via `POST /people/:id/archive`. */
export const personStatusEnum = z.enum(['active', 'onboarding', 'inactive', 'stood_down']);
/**
 * `nqa_position` — migration 0019.
 *
 * Closed enum from ACECQA's WRJSONSchemav1.10 `Position` field. This is the
 * ACECQA-facing classification (what role the worker plays in the service)
 * and is distinct from our internal `person_type` (which captures the
 * enrolment shape: volunteer / practicum_student / observer / staff).
 */
export const nqaPositionEnum = z.enum([
    'Educator',
    'Volunteer',
    'Student',
    'Non-Educator Staff',
    'Early Childhood Teacher',
    'Co-ordinator',
    'Assistant',
    'Contractor',
]);
/**
 * `nqa_non_educator_role` — migration 0019. Only meaningful when
 * `nqa_position === 'Non-Educator Staff'`; DB CHECK enforces null otherwise.
 */
export const nqaNonEducatorRoleEnum = z.enum([
    'Bus Driver',
    'Centre Director',
    'Cook',
    'Cleaner',
    'Gardener',
    'Other',
]);
/** `nqa_nature_of_employment` — migration 0019. Direct = engaged by the
 *  service; Indirect = engaged via a third party (agency / contractor firm). */
export const nqaNatureOfEmploymentEnum = z.enum(['Direct', 'Indirect']);
/** `nqa_address_type` — migration 0019. */
export const nqaAddressTypeEnum = z.enum(['Residential', 'Postal', 'Other']);
/** `au_state_code` — migration 0019. Australian states + territories. */
export const auStateCodeEnum = z.enum(['ACT', 'NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NT']);
/**
 * `requirement_pattern` — migration 0023 (M10 Compliance requirement
 * rework), extended in 0026 with `volunteer_attestation`. Four patterns
 * that the volunteer-facing flows map to:
 *   - `upload` — volunteer uploads file(s) (e.g. WWCC screenshot, training certificate).
 *   - `sign` — volunteer reads a linked Document + types name; produces
 *     an `acknowledgement_signatures` row.
 *   - `admin_attestation` — admin uploads on volunteer's behalf
 *     (prohibition notice check today; future government-registry sync).
 *     Not surfaced on `/me`.
 *   - `volunteer_attestation` — volunteer reads admin-authored markdown
 *     and clicks a configurable confirm button on `/me`. No file, no
 *     typed-name signature, no email step-up code (cheaper than `sign`,
 *     weaker than `sign`, deliberately so).
 */
export const requirementPatternEnum = z.enum([
    'upload',
    'sign',
    'admin_attestation',
    'volunteer_attestation',
]);
/** `compliance_record_status` — migration 0006, slimmed in migration 0025. */
export const complianceRecordStatusEnum = z.enum([
    'pending',
    'submitted',
    'verified',
    'expiring_soon',
    'expired',
    'archived',
    'superseded',
]);
/** `verified_by_kind` — migration 0006. */
export const verifiedByKindEnum = z.enum(['admin', 'system']);
/** `still_active_response` — migration 0006. Column is nullable; this
 *  schema reflects the enum values only, not the null state. */
export const stillActiveResponseEnum = z.enum(['yes', 'no', 'needs_help']);
/** `evidence_uploader_kind` — migration 0007. */
export const evidenceUploaderKindEnum = z.enum(['volunteer', 'admin']);
/** `evidence_source` — migration 0007. */
export const evidenceSourceEnum = z.enum(['intake_form', 'self_service_upload', 'admin_upload']);
/** `reminder_channel` — migration 0009. `email` is the only v1 value. */
export const reminderChannelEnum = z.enum(['email']);
/** `reminder_delivery_status` — migration 0009. */
export const reminderDeliveryStatusEnum = z.enum(['sent', 'bounced', 'failed']);
/** `audit_actor_kind` — migration 0010. */
export const auditActorKindEnum = z.enum(['admin', 'volunteer', 'system']);
/**
 * Generic export-list status enum used by the audit-pack list endpoint.
 * Same shape as `audit_pack_status` in migration 0022 (no `dead_letter`
 * — that was the M9-retired queue's terminal state).
 */
export const jobRunStatusEnum = z.enum(['pending', 'running', 'succeeded', 'failed']);
/** `audit_pack_status` — migration 0022. */
export const auditPackStatusEnum = z.enum(['pending', 'running', 'succeeded', 'failed']);
/** `magic_link_purpose` — migrations 0012 + 0027 + 0029. The `intake`,
 *  `still_active`, and `renewal` token purposes were retired in April
 *  2026 — those flows moved behind the volunteer self-service `/me`
 *  session (via the standard `/sign-in` email-OTP). M13 retired the
 *  per-delivery `/ack/:token` URL too: acknowledgement is now an inline
 *  `/me` action with a 6-digit email step-up code per signing attempt
 *  (`acknowledgement_step_up`, migration 0029 — renamed from the old
 *  `acknowledgement` value). `admin_sign_in_code` (migration 0027) is
 *  the admin-side equivalent of `self_service_sign_in` — same email-OTP
 *  shape, but the row references `admin_users` rather than `people`. */
export const magicLinkPurposeEnum = z.enum([
    'acknowledgement_step_up',
    'self_service_sign_in',
    'admin_sign_in_code',
]);
// -----------------------------------------------------------------------------
// Entity schemas — one per table in `migrations/`.
// Order matches PRD 03 (people, item_types, records, evidence, signatures,
// reminders, documents, admin_users, audit_events, scheduled_job_runs,
// magic_link_tokens, volunteer_sessions) to keep this file scannable
// alongside the PRD.
// -----------------------------------------------------------------------------
// 1. people  (migration 0005 + NQA ITS fields from 0019 + 0034 simplification)
export const personSchema = z.object({
    person_id: idSchema,
    /** Projected at SELECT time from `concat_ws(' ', first_name,
     *  middle_name, last_name)`; the stored column was dropped in
     *  migration 0034. The DTO field name stays so frontends keep
     *  working unchanged. */
    full_name: z.string().min(1),
    preferred_name: z.string().nullable(),
    /** Null until the volunteer supplies it at intake (PRD 06
     *  §Volunteer experience #1). Admin can later patch it. */
    date_of_birth: isoDateSchema.nullable(),
    contact_email: citextEmailSchema,
    person_type: personTypeEnum,
    role_tags: z.array(z.string()),
    direct_child_contact: z.boolean(),
    /** Null until the admin captures it at create, patches it later, or
     *  the volunteer confirms it at intake (PRD 03 §1). */
    start_date: isoDateSchema.nullable(),
    end_date: isoDateSchema.nullable(),
    status: personStatusEnum,
    nqa_its_entry_id: z.string().nullable(),
    /** Timestamp of the admin's last "Mark NQA ITS synced" action. Null
     *  while the person has never been synced. The dashboard derives
     *  "needs sync" from this + people.updated_at. */
    nqa_its_synced_at: isoDateTimeSchema.nullable(),
    notes: z.string().nullable(),
    deleted_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    // NQA ITS Worker Register fields (migration 0019) --------------------------
    // Captured so the admin can copy them into the ACECQA portal manually. No
    // automated emission in v1. `FDCLocationType` is not stored — ISMS is an
    // independent school, not an FDC service.
    title: z.string().nullable(),
    first_name: z.string().min(1),
    middle_name: z.string().nullable(),
    last_name: z.string().min(1),
    names_previously_known_as: z.string().nullable(),
    alias_other_names_known_by: z.string().nullable(),
    /** Single phone slot — used for both the phone and mobile fields in
     *  the ACECQA portal (the admin enters this number into both slots
     *  at sync time). Migration 0034 collapsed the earlier
     *  contact_phone / phone / mobile_phone trio. */
    mobile_phone: z.string().nullable(),
    nqa_position: nqaPositionEnum.nullable(),
    non_educator_role: nqaNonEducatorRoleEnum.nullable(),
    nature_of_employment: nqaNatureOfEmploymentEnum,
    on_probationary_period: z.boolean().nullable(),
    wwcc_exemption: z.boolean(),
    wwcc_exemption_reason: z.string().nullable(),
    no_qualification_training: z.boolean(),
    address_type: nqaAddressTypeEnum.nullable(),
    address_line_1: z.string().nullable(),
    address_line_2: z.string().nullable(),
    address_suburb: z.string().nullable(),
    address_state: auStateCodeEnum.nullable(),
    address_postcode: z.string().nullable(),
});
// 2. compliance_requirements  (migration 0004 + 0023 rework)
export const complianceRequirementSchema = z.object({
    requirement_id: idSchema,
    name: z.string().min(1),
    short_code: z.string().min(1),
    pattern: requirementPatternEnum,
    active: z.boolean(),
    /** Volunteer-facing markdown copy (PRD 01 §5). Empty string when the
     *  requirement is `admin_attestation` and never surfaces on `/me`. */
    markdown_explainer: z.string(),
    /** Required when `pattern === 'sign'`; null otherwise. DB CHECK
     *  enforces the same invariant. */
    linked_document_id: idSchema.nullable(),
    /** Null for ack rows (the policy republish flow drives expiry via
     *  bulk-invalidate) and admin_attestation rows whose cadence isn't
     *  date-driven. Otherwise the daily-reminder cadence works back from
     *  `verified_on + expiry_duration_days`. */
    expiry_duration_days: z.number().int().positive().nullable(),
    /** Per-requirement button copy rendered on the volunteer's `/me`
     *  attestation row and quoted verbatim in the daily-reminder email
     *  (migration 0026). The DB default is `'I confirm'`; admins typically
     *  override to something contextual ("I've reviewed my details",
     *  "I've read the handbook"). Carried on every requirement row even
     *  for non-attestation patterns so the schema stays uniform; only
     *  `pattern='volunteer_attestation'` actually surfaces it. */
    confirm_button_label: z.string().min(1),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
// 3. compliance_records  (migration 0006 + 0023 FK rename)
export const complianceRecordSchema = z.object({
    record_id: idSchema,
    person_id: idSchema,
    /** FK to `compliance_requirements.requirement_id` (renamed from
     *  `item_type_id` in migration 0023, M10). */
    requirement_id: idSchema,
    status: complianceRecordStatusEnum,
    completed_on: isoDateSchema.nullable(),
    evidence_id: idSchema.nullable(),
    acknowledgement_signature_id: idSchema.nullable(),
    expiry_date: isoDateSchema.nullable(),
    verified_on: isoDateTimeSchema.nullable(),
    verified_by_id: idSchema.nullable(),
    verified_by_kind: verifiedByKindEnum.nullable(),
    verification_notes: z.string().nullable(),
    superseded_by_id: idSchema.nullable(),
    still_active_response: stillActiveResponseEnum.nullable(),
    still_active_response_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
// 4. evidence  (migration 0007 + 0023 FK rename)
export const evidenceSchema = z.object({
    evidence_id: idSchema,
    person_id: idSchema,
    /** FK to `compliance_requirements.requirement_id` (renamed from
     *  `item_type_id` in migration 0023). */
    requirement_id: idSchema,
    storage_key: z.string().min(1),
    storage_version_id: z.string().nullable(),
    original_filename: z.string().min(1),
    mime_type: z.string().min(1),
    size_bytes: z.union([
        z.number().int().positive(),
        z.bigint().positive(),
        z.string().regex(/^\d+$/),
    ]),
    sha256: hex64Schema,
    uploaded_at: isoDateTimeSchema,
    uploaded_by_kind: evidenceUploaderKindEnum,
    /**
     * Polymorphic: resolves against `admin_users.admin_user_id` when
     * `uploaded_by_kind='admin'`, and against `people.person_id` when
     * `uploaded_by_kind='volunteer'`. Nullable per PRD 03 §4 for legacy
     * / system-generated inserts that predate actor capture.
     */
    uploaded_by_id: idSchema.nullable(),
    source: evidenceSourceEnum,
});
// 5. acknowledgement_signatures  (migration 0008)
export const acknowledgementSignatureSchema = z.object({
    signature_id: idSchema,
    person_id: idSchema,
    document_id: idSchema,
    document_sha256: hex64Schema,
    document_storage_key: z.string().min(1),
    acknowledgement_text: z.string().min(1),
    acknowledgement_text_version: z.string().min(1),
    typed_name: z.string().min(1),
    submitted_at_utc: isoDateTimeSchema,
    submitted_at_client: z.string().nullable(),
    client_ip: inetSchema.nullable(),
    user_agent: z.string().nullable(),
    token_jti: z.string().min(1),
    token_issued_at: isoDateTimeSchema,
    token_used_at: isoDateTimeSchema,
    page_render_id: z.string().min(1),
    confirmations: z.record(z.string(), z.unknown()),
    /** JTI of the consumed `acknowledgement_step_up` `magic_link_tokens`
     *  row (migration 0029). The audit join `acknowledgement_signatures
     *  → magic_link_tokens` recovers the per-event identification factor
     *  (which email the code was delivered to, when it was redeemed)
     *  years later. PRD 05 §4. */
    step_up_token_jti: z.string().min(1),
    /** Timestamp at which the step-up code was redeemed; coincides with
     *  the signing event itself (the redeem and the signature insert run
     *  in one DB transaction). */
    step_up_consumed_at: isoDateTimeSchema,
    created_at: isoDateTimeSchema,
});
// 6. reminders  (migration 0009)
export const reminderSchema = z.object({
    reminder_id: idSchema,
    record_id: idSchema,
    person_id: idSchema,
    /** Negative values represent post-expiry escalations (PRD 02). */
    offset_days: z.number().int(),
    template: z.string().min(1),
    sent_at: isoDateTimeSchema,
    channel: reminderChannelEnum,
    delivery_status: reminderDeliveryStatusEnum,
    provider_message_id: z.string().nullable(),
});
// 7. documents  (migration 0003 + 0030 policy_number drop + 0031 publish→archive
//                + 0032 metadata-fields drop)
//
// Every row is active from creation: the artefact CHECK requires
// `rendered_pdf_storage_key` and `rendered_pdf_sha256` to be populated
// unconditionally (migration 0031, narrowed in 0032). When a version is
// retired, the admin archives it via `POST /admin/documents/:id/archive`.
export const documentSchema = z.object({
    document_id: idSchema,
    title: z.string().min(1),
    version: z.string().min(1),
    rendered_pdf_storage_key: z.string().min(1),
    rendered_pdf_sha256: hex64Schema,
    archived_by_id: idSchema.nullable(),
    archived_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
// 8. admin_users  (migrations 0002 + 0027 + 0033)
export const adminUserSchema = z.object({
    admin_user_id: idSchema,
    email: citextEmailSchema,
    full_name: z.string().min(1),
    /** TOTP secret. Null in the bootstrap window between row create and
     *  the first sign-in's forced enrolment confirm — every admin
     *  endpoint short-circuits with 412 `totp_enrolment_required` while
     *  this is null. Migration 0027 dropped `password_hash` entirely;
     *  TOTP is the only secret factor. */
    mfa_secret: z.string().nullable(),
    is_active: z.boolean(),
    created_at: isoDateTimeSchema,
    last_login_at: isoDateTimeSchema.nullable(),
});
// 9. audit_events  (migration 0010)
export const auditEventSchema = z.object({
    audit_event_id: idSchema,
    actor_kind: auditActorKindEnum,
    /** Null exactly when `actor_kind = 'system'` (DB CHECK). */
    actor_id: idSchema.nullable(),
    actor_ip: inetSchema.nullable(),
    actor_user_agent: z.string().nullable(),
    action: z.string().min(1),
    entity_table: z.string().min(1),
    entity_id: idSchema.nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    request_id: z.string().nullable(),
    created_at: isoDateTimeSchema,
});
// 10. cron_runs (migration 0022 + 0028) — replaces the M9-retired scheduled_job_runs.
//     One row per EventBridge Scheduler invocation of `/internal/cron/<job>`.
//     The dashboard cron-health card reads this; the body of the run is
//     the daily reminder or weekly digest handler. The `(job_name, date_bucket)`
//     UNIQUE index added in 0028 is the dedup mechanism for EventBridge retries.
export const cronRunStatusEnum = z.enum(['running', 'succeeded', 'failed']);
export const cronRunJobNameEnum = z.enum(['daily_reminder', 'weekly_digest']);
export const cronRunSchema = z.object({
    run_id: idSchema,
    job_name: cronRunJobNameEnum,
    started_at: isoDateTimeSchema,
    finished_at: isoDateTimeSchema.nullable(),
    status: cronRunStatusEnum,
    /** weekly_digest only: handler ran, nothing actionable. */
    skipped_empty: z.boolean(),
    error_message: z.string().nullable(),
    correlation_id: z.string().min(1),
    created_at: isoDateTimeSchema,
});
// 11. magic_link_tokens  (migrations 0012 + 0017 + 0027)
export const magicLinkTokenSchema = z.object({
    token_jti: z.string().min(1),
    /** Set for every purpose except `admin_sign_in_code` (XOR with
     *  `admin_user_id` enforced by the DB CHECK on migration 0027). */
    person_id: idSchema.nullable(),
    /** Set for `admin_sign_in_code` only — the admin-side equivalent of
     *  `self_service_sign_in`. Migration 0027. */
    admin_user_id: idSchema.nullable(),
    purpose: magicLinkPurposeEnum,
    payload: z.record(z.string(), z.unknown()),
    issued_at: isoDateTimeSchema,
    expires_at: isoDateTimeSchema,
    used_at: isoDateTimeSchema.nullable(),
    delivered_to_email: citextEmailSchema,
    /**
     * HMAC-SHA256 of the 6-digit OTP code for `self_service_sign_in` and
     * `admin_sign_in_code` tokens (PRD 04 §Identity and access). Null for
     * the action purposes where `token_jti` is itself the secret.
     */
    code_hash: z.string().nullable(),
});
// 12. volunteer_sessions  (migration 0013)
export const volunteerSessionSchema = z.object({
    session_id: z.string().min(1),
    person_id: idSchema,
    created_at: isoDateTimeSchema,
    last_seen_at: isoDateTimeSchema,
    expires_at: isoDateTimeSchema,
    revoked_at: isoDateTimeSchema.nullable(),
    created_from_token_jti: z.string().min(1),
    created_ip: inetSchema.nullable(),
    created_user_agent: z.string().nullable(),
});
//# sourceMappingURL=entities.js.map